import {
  Spin,
  Form,
  Row,
  Col,
  message,
  Input,
  Select,
  Alert,
  Button,
  Switch,
  Modal,
  Tooltip,
} from "antd";
import {
  LockOutlined,
  UploadOutlined,
  CopyOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  FormItem,
  ErrorItem,
  ButtonItem,
  SelectItem,
  InputItem,
  InputNumberItem,
} from "../../../../Components/Items";
import DynamicCardForm from "../../../../Components/DynamicCardForm";
import RichTextEditor from "../../../../Components/RichTextEditor";
import SegmentedItem from "../../../../Components/SegmentedItem";
import axios from "axios";

const { Option } = Select;

const METODO_OPTIONS = [
  { label: "Conekta", value: "conekta" },
  { label: "Mattilda", value: "mattilda" },
  { label: "Transferencia fija (CLABE)", value: "transferenciaFija" },
];

// Prompt de ejemplo para generar con IA las instrucciones de pago (HTML).

const PROMPT_INSTRUCCIONES_PAGO_HTML = `Genera el contenido HTML de las INSTRUCCIONES DE PAGO por transferencia para una tienda online escolar. Explica al comprador cómo completar su pago: qué banco/CLABE usar, qué poner como referencia/concepto, y cualquier aclaración relevante (p.ej. tiempos de reflejo del banco, qué hacer si no llega el pago, a quién contactar). Reglas:

DÓNDE SE MUESTRA (IMPORTANTE)
Este HTML se usa en TRES lugares distintos:
1. Checkout / ficha de pago en la página web — aquí SÍ hay Tailwind cargado (clase "prose" alrededor), así que las clases de la lista de abajo se ven bien.
2. Ficha de pago impresa (PDF) — aquí NO hay Tailwind. Las clases se ignoran; solo sobreviven las etiquetas semánticas (<p>, <strong>, <ul>, etc.) y los estilos inline (style="...").
3. Correo de confirmación — igual que el PDF: sin Tailwind, solo etiquetas semánticas y estilos inline.
Por eso: el CONTENIDO (el texto en sí) debe entenderse perfectamente aunque no se vea ningún color ni fondo. Usa clases solo como adorno extra para la vista web, nunca para transmitir información que se perdería en el PDF o el correo (p.ej. no uses solo un color para marcar algo "urgente"; dilo también con texto en <strong>).

ESTRUCTURA
- Devuelve SOLO el contenido (sin <html>, <head>, <body>, <style>, <script>).
- Usa HTML semántico: <p>, <ul>, <ol>, <li>, <strong>, <em>, <a href="..." target="_blank" rel="noopener noreferrer">, <hr>.
- NO uses <h1>/<h2> (el título "Ficha de pago" ya lo pone el sistema).
- Sé breve y concreto: un par de párrafos y, si hace falta, una lista corta de pasos o aclaraciones.

══════════════════════════════════════════════════════════════════
COLORES — hay DOS formas, según el color que quieras (solo afectan la vista WEB):
══════════════════════════════════════════════════════════════════
1) COLORES DE LA MARCA → usa CLASES de Tailwind (de la lista permitida):
   Texto: text-primary-700 text-primary-500 text-secondary-700 text-tertiary-700
   Fondo: bg-primary-50 bg-primary-500 bg-secondary-50 bg-tertiary-50
   Ejemplo: <p class="text-primary-700 font-semibold">Texto en color de marca</p>

2) CUALQUIER OTRO COLOR → usa ESTILO INLINE (no una clase):
   Texto: <span style="color: #e60000;">...</span>
   Fondo: <span style="background-color: #fff3cd;">...</span>
   Ejemplo: <p style="color: #6b21a8;">Texto en un morado personalizado</p>

REGLA: en un mismo elemento NO mezcles clase de color Y style de color
(si pones ambos, gana el inline). Elige uno por elemento.
══════════════════════════════════════════════════════════════════

CLASES TAILWIND PERMITIDAS (para todo lo demás: tamaño, espaciado, layout — solo vista web)
- Alineación: text-left text-center text-right text-justify
- Tamaño/peso: text-sm text-base text-lg text-xl font-medium font-semibold font-bold italic uppercase tracking-wide leading-relaxed
- Colores texto marca: text-primary-700 text-primary-500 text-secondary-700 text-tertiary-700 text-neutral-500 text-neutral-600 text-neutral-700
- Fondos marca: bg-primary-50 bg-primary-500 bg-secondary-50 bg-tertiary-50 bg-neutral-50 bg-neutral-100
- Bordes/radio: border border-2 border-l-4 border-primary-500 border-neutral-200 rounded rounded-lg rounded-xl
- Espaciado: p-2 p-3 p-4 p-6 px-4 py-2 py-3 my-4 my-6 mb-4 mt-4
- Layout: flex items-center gap-2 gap-3 w-full
- Listas: list-disc list-decimal list-inside

PROHIBIDO
- NO inventes CLASES fuera de la lista de arriba (no se genera el CSS y no se ven en la web; y no existen en el PDF/correo de todos modos).
- NO uses colores arbitrarios como CLASE (text-[#123456] no funciona) → usa style inline.
- NO uses <script>, <table>, <img> ni posicionamiento/animaciones complejas.

Escribe las instrucciones de pago para: [banco/CLABE/beneficiario ya se muestran aparte; aquí solo aclaraciones adicionales, p.ej. plazo de reflejo, qué hacer si no coincide el nombre del beneficiario, contacto de soporte, etc.].`;

function InstruccionesEditor({ value, onChange, placeholder }) {
  const [htmlMode, setHtmlMode] = React.useState(false);

  const copiarPrompt = () => {
    const exito = () => message.success("Prompt copiado al portapapeles");
    const fallo = () => message.error("No se pudo copiar el prompt");

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(PROMPT_INSTRUCCIONES_PAGO_HTML).then(exito, fallo);
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = PROMPT_INSTRUCCIONES_PAGO_HTML;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        exito();
      } catch (e) {
        fallo();
      }
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <Tooltip title="Copiar prompt de ejemplo para generar las instrucciones con IA">
          <button
            type="button"
            onClick={copiarPrompt}
            style={{
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 6px",
              border: "none",
              background: "transparent",
              color: "#8c8c8c",
              cursor: "pointer",
            }}
          >
            <CopyOutlined /> Prompt IA
          </button>
        </Tooltip>
        <button
          type="button"
          onClick={() => setHtmlMode((prev) => !prev)}
          style={{
            fontSize: 12,
            padding: "2px 10px",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            background: htmlMode ? "#1890ff" : "#fff",
            color: htmlMode ? "#fff" : "#595959",
            cursor: "pointer",
          }}
        >
          {htmlMode ? "Vista visual" : "HTML crudo"}
        </button>
      </div>
      {htmlMode ? (
        <Input.TextArea
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          rows={8}
          style={{ fontFamily: "monospace", fontSize: 12 }}
          placeholder="Pega o escribe el HTML aquí..."
        />
      ) : (
        <RichTextEditor
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          height={180}
        />
      )}
      <div style={{ marginTop: 4, fontSize: 12, color: "#8c8c8c" }}>
        Estilos recomendados: usa solo etiquetas simples (<code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>,{" "}
        <code>&lt;a href&gt;</code>, <code>&lt;ul&gt;/&lt;li&gt;</code>, <code>&lt;br&gt;</code>) y colores de texto
        básicos. Evita <code>&lt;script&gt;</code>, tablas, imágenes o estilos complejos (posicionamiento,
        fondos, animaciones): esto se muestra en el checkout, la ficha de pago (impresa) y el correo, y no todos
        esos entornos renderizan CSS avanzado igual.
      </div>
    </div>
  );
}

class ApisConfiguracion extends Component {
  formRef = React.createRef();
  state = {
    loading: false,
    formConCambios: false,
    // Configuración por escuela
    escuelas: [],
    escuelaSeleccionadaId: null,
    configEscuelaExiste: false,
    configEscuelaActiva: false,
    loadingEscuela: false,
  };

  componentDidMount() {
    this.consultarConfiguracion();
    if (this.props.escuela) {
      this.cargarEscuelas();
    }
  }

  consultarConfiguracion = () => {
    this.setState({ loading: true });

    axios
      .post(
        "configuracionTiendaOnline/obtenerConfiguracionApis",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2 && res.data.data) {
          const config = res.data.data;

          if (config.mattilda) {
            this.formRef.current?.setFieldsValue({
              mattilda: {
                activo: !!config.mattilda.activo,
                privateKey: config.mattilda.privateKey || "",
                ambiente: config.mattilda.ambiente || "sandbox",
                merchantAccountId: config.mattilda.merchantAccountId || "",
              },
            });
          }

          this.formRef.current.setFieldsValue({
            conekta: {
              llavePublica: config.conekta?.llavePublica || "",
              llavePrivada: config.conekta?.llavePrivada || "",
              metodosDePago: config.conekta?.metodosDePago || [
                "card",
                "cash",
                "bank_transfer",
              ],
            },
          });
        }
      })
      .catch((err) => {
        console.error("Error cargando config apis", err);
        message.error("Error al cargar la configuración de APIs");
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  cargarEscuelas = () => {
    axios
      .post(
        "escuelas/selectTodasEscuelas",
        { empresaId: this.props.empresaId, valor: 1, pageSize: 1000 },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ escuelas: res.data.data || [] });
        }
      })
      .catch(() => {});
  };

  cargarConfigEscuela = (escuelaId) => {
    this.setState({ loadingEscuela: true });
    axios
      .post(
        "configuracionTiendaOnline/obtenerConfiguracionPagoEscuela",
        { empresaId: this.props.empresaId, escuelaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          if (res.data.data) {
            this._aplicarConfigEscuelaAlForm(res.data.data);
            this.setState({
              configEscuelaExiste: true,
              configEscuelaActiva: !!res.data.data.activo,
              formConCambios: false,
            });
          } else {
            this._aplicarConfigEscuelaAlForm(null);
            this.setState({
              configEscuelaExiste: false,
              configEscuelaActiva: false,
              formConCambios: false,
            });
          }
        }
      })
      .catch(() => message.error("Error al cargar la configuración de la escuela"))
      .finally(() => this.setState({ loadingEscuela: false }));
  };

  _aplicarConfigEscuelaAlForm = (config) => {
    this.formRef.current?.resetFields();
    this.formRef.current?.setFieldsValue({
      metodo: config?.metodo || "conekta",
      conekta: {
        llavePublica: config?.conekta?.llavePublica || "",
        llavePrivada: config?.conekta?.llavePrivada || "",
        metodosDePago: config?.conekta?.metodosDePago || [
          "card",
          "cash",
          "bank_transfer",
        ],
      },
      mattilda: {
        privateKey: config?.mattilda?.privateKey || "",
        ambiente: config?.mattilda?.ambiente || "sandbox",
        merchantAccountId: config?.mattilda?.merchantAccountId || "",
      },
      transferenciaFija: {
        instrucciones: config?.transferenciaFija?.instrucciones || "",
        diasExpiracion: config?.transferenciaFija?.diasExpiracion || 7,
        mostrarAvisoExpiracion: config?.transferenciaFija?.mostrarAvisoExpiracion !== false,
        leyendaBotonCheckout: config?.transferenciaFija?.leyendaBotonCheckout || "Ir a generar ficha de pago",
        leyendaBotonFicha: config?.transferenciaFija?.leyendaBotonFicha || "Generar ficha de pago",
        bancos: config?.transferenciaFija?.bancos || [],
      },
      compraDirecta: {
        activa: !!config?.compraDirecta?.activa,
      },
    });
  };

  onSelectEscuela = (nuevaEscuelaId) => {
    if (this.state.formConCambios) {
      Modal.confirm({
        title: "Tienes cambios sin guardar",
        content: "¿Descartar los cambios y cambiar de escuela?",
        okText: "Descartar y cambiar",
        cancelText: "Cancelar",
        centered: true,
        onOk: () => {
          this._cambiarEscuela(nuevaEscuelaId);
        },
      });
    } else {
      this._cambiarEscuela(nuevaEscuelaId);
    }
  };

  _cambiarEscuela = (escuelaId) => {
    this.setState({
      escuelaSeleccionadaId: escuelaId,
      configEscuelaExiste: false,
      configEscuelaActiva: false,
      formConCambios: false,
    });
    this.cargarConfigEscuela(escuelaId);
  };

  onDeseleccionarEscuela = () => {
    if (this.state.formConCambios) {
      Modal.confirm({
        title: "Tienes cambios sin guardar",
        content: "¿Descartar los cambios y volver a la configuración general?",
        okText: "Descartar y volver",
        cancelText: "Cancelar",
        centered: true,
        onOk: () => this._volverAEmpresa(),
      });
    } else {
      this._volverAEmpresa();
    }
  };

  _volverAEmpresa = () => {
    this.setState({
      escuelaSeleccionadaId: null,
      configEscuelaExiste: false,
      configEscuelaActiva: false,
      formConCambios: false,
    });
    this.formRef.current?.resetFields();
    this.consultarConfiguracion();
  };

  desactivarConfigEscuela = () => {
    Modal.confirm({
      title: "¿Usar configuración general?",
      content:
        "La escuela pasará a cobrar con la configuración general de la empresa. La configuración propia se conserva y podrás volver a activarla cuando quieras.",
      okText: "Usar config general",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      centered: true,
      onOk: () => {
        this.setState({ loadingEscuela: true });
        axios
          .post(
            "configuracionTiendaOnline/actualizarConfiguracionPagoEscuela",
            {
              empresaId: this.props.empresaId,
              escuelaId: this.state.escuelaSeleccionadaId,
              activo: false,
            },
            { headers: this.props.headersToken },
          )
          .then((res) => {
            if (res.data.success === 2) {
              message.success("Escuela usando configuración general de la empresa");
              this.setState({ configEscuelaActiva: false });
            }
          })
          .catch(() => message.error("Error al actualizar la configuración"))
          .finally(() => this.setState({ loadingEscuela: false }));
      },
    });
  };

  activarConfigEscuela = () => {
    this.setState({ loadingEscuela: true });
    axios
      .post(
        "configuracionTiendaOnline/actualizarConfiguracionPagoEscuela",
        {
          empresaId: this.props.empresaId,
          escuelaId: this.state.escuelaSeleccionadaId,
          activo: true,
        },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          message.success("Configuración propia de la escuela activada");
          this.setState({ configEscuelaActiva: true });
        }
      })
      .catch(() => message.error("Error al activar la configuración"))
      .finally(() => this.setState({ loadingEscuela: false }));
  };

  onFinish = async (values) => {
    this.setState({ loading: true });

    if (!this.props.permisoAnadirModificarTokens) {
      if (values.conekta) {
        delete values.conekta.llavePublica;
        delete values.conekta.llavePrivada;
      }
      if (values.mattilda) {
        delete values.mattilda.privateKey;
        delete values.mattilda.merchantAccountId;
      }
    }

    const { escuelaSeleccionadaId } = this.state;

    if (escuelaSeleccionadaId) {
      return axios
        .post(
          "configuracionTiendaOnline/actualizarConfiguracionPagoEscuela",
          {
            empresaId: this.props.empresaId,
            escuelaId: escuelaSeleccionadaId,
            activo: true,
            ...values,
          },
          { headers: this.props.headersToken },
        )
        .then((res) => {
          if (res.data.success === 2) {
            message.success("Configuración de pago de escuela guardada");
            this.setState({
              formConCambios: false,
              configEscuelaExiste: true,
              configEscuelaActiva: true,
            });
            return true;
          }
          message.error(res.data.message || "No se pudo guardar la configuración");
          return false;
        })
        .catch(() => {
          message.error("Error al guardar");
          return false;
        })
        .finally(() => this.setState({ loading: false }));
    }

    const payload = {
      empresaId: this.props.empresaId,
      ...values,
    };

    return axios
      .post("configuracionTiendaOnline/actualizarConfiguracionApis", payload, {
        headers: this.props.headersToken,
      })
      .then((res) => {
        if (res.data.success === 2) {
          message.success("Se ha modificado la configuración de cobro");
          this.setState({ formConCambios: false });
          return true;
        } else {
          message.error("No se pudo modificar la configuración");
          return false;
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Error al guardar");
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onFinishFailed = (e) => {
    ErrorItem(e);
  };

  tieneCambios = () => {
    return this.state.formConCambios;
  };

  guardarDesdePadre = () => {
    return this.formRef.current
      .validateFields()
      .then((values) => {
        return this.onFinish(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        return false;
      });
  };

  handleValuesChange = (changedValues) => {
    if (!this.state.formConCambios) {
      this.setState({ formConCambios: true });
    }
    if (changedValues?.metodo && changedValues.metodo !== "transferenciaFija") {
      const compraDirectaActual = this.formRef.current?.getFieldValue([
        "compraDirecta",
        "activa",
      ]);
      if (compraDirectaActual) {
        this.formRef.current.setFieldsValue({
          compraDirecta: { activa: false },
        });
      }
    }
  };

  descartarCambios = () => {
    this.setState({ formConCambios: false });
    if (this.state.escuelaSeleccionadaId) {
      this.cargarConfigEscuela(this.state.escuelaSeleccionadaId);
    } else {
      this.formRef.current?.resetFields();
      this.consultarConfiguracion();
    }
  };

  cargarPemDesdeArchivo = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pem,.key,.txt";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const content = ev.target.result;
        this.formRef.current.setFieldsValue({
          mattilda: {
            ...this.formRef.current.getFieldValue("mattilda"),
            privateKey: content.trim(),
          },
        });
        if (!this.state.formConCambios) {
          this.setState({ formConCambios: true });
        }
        message.success("Archivo PEM cargado");
      };
      reader.readAsText(file);
    };
    input.click();
  };

  renderCamposConekta = (credencialesBloqueadas) => (
    <>
      <FormItem
        label="Llave Pública"
        name={["conekta", "llavePublica"]}
        margin
        tooltip=""
      >
        <Input.Password
          placeholder="Ej. key_XXXXXXXXX"
          visibilityToggle={false}
          disabled={credencialesBloqueadas}
          prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
        />
      </FormItem>

      <FormItem
        label="Llave Privada"
        name={["conekta", "llavePrivada"]}
        margin
        tooltip=""
      >
        <Input.Password
          placeholder="Ej. key_XXXXXXXXX"
          visibilityToggle={false}
          disabled={credencialesBloqueadas}
          prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
        />
      </FormItem>

      <FormItem
        label="Métodos de Pago Permitidos"
        name={["conekta", "metodosDePago"]}
        margin
        tooltip="Selecciona qué opciones le aparecerán al cliente al momento de pagar."
      >
        <SelectItem
          mode="multiple"
          placeholder="Selecciona los métodos"
          style={{ width: "100%" }}
        >
          <Option value="card">Tarjeta de Crédito / Débito</Option>
          <Option value="cash">Efectivo (Oxxo, etc.)</Option>
          <Option value="bank_transfer">Transferencia (SPEI)</Option>
        </SelectItem>
      </FormItem>
    </>
  );

  renderCamposMattilda = (credencialesBloqueadas) => (
    <>
      <FormItem
        label={
          <span>
            Clave Privada PEM&nbsp;
            <Button
              size="small"
              icon={<UploadOutlined />}
              disabled={credencialesBloqueadas}
              onClick={this.cargarPemDesdeArchivo}
            >
              Cargar .pem
            </Button>
          </span>
        }
        name={["mattilda", "privateKey"]}
        margin
        tooltip="Llave privada en formato PEM (ES512) descargada del dashboard de Mattilda."
      >
        <Input.TextArea
          rows={5}
          disabled={credencialesBloqueadas}
          placeholder={
            "-----BEGIN EC PRIVATE KEY-----\n...\n-----END EC PRIVATE KEY-----"
          }
          style={{ fontFamily: "monospace", fontSize: 11 }}
        />
      </FormItem>

      <FormItem
        label="Merchant Account ID (opcional)"
        name={["mattilda", "merchantAccountId"]}
        margin
        tooltip="ID de la cuenta de comercio si usas multi-merchant."
      >
        <InputItem placeholder="Ej. mi-cuenta" disabled={credencialesBloqueadas} />
      </FormItem>

      <FormItem
        label="Ambiente"
        name={["mattilda", "ambiente"]}
        margin
        tooltip="Selecciona Sandbox para pruebas o Producción para cobros reales."
      >
        <SelectItem style={{ width: "100%" }}>
          <Option value="sandbox">Sandbox (pruebas)</Option>
          <Option value="production">Producción</Option>
        </SelectItem>
      </FormItem>
    </>
  );

  renderFormEmpresa = (credencialesBloqueadas) => (
    <Row gutter={24}>
      <Col span={12}>
        <DynamicCardForm
          title={"Conekta"}
          style={{
            margin: "0 auto 12px auto",
            maxWidth: "98%",
          }}
        >
          {this.renderCamposConekta(credencialesBloqueadas)}
        </DynamicCardForm>
      </Col>
      <Col span={12}>
        {this.props.escuela && (
          <DynamicCardForm
            title={"Mattilda Payments (Escuela)"}
            style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
          >
            <Alert
              message="Pasarela de pagos para Escuela"
              description="Configura Mattilda como pasarela de pagos. Al tener credenciales válidas, el checkout usará Mattilda en lugar de Conekta."
              type="info"
              showIcon
              style={{ marginBottom: 15 }}
            />

            <FormItem
              label="Activar Mattilda"
              name={["mattilda", "activo"]}
              margin
              valuePropName="checked"
              tooltip="Activa o desactiva el cobro con Mattilda en el checkout."
            >
              <Switch />
            </FormItem>

            {this.renderCamposMattilda(credencialesBloqueadas)}
          </DynamicCardForm>
        )}
      </Col>
    </Row>
  );

  renderFormEscuela = (credencialesBloqueadas) => (
    <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.metodo !== cur?.metodo}>
      {({ getFieldValue }) => {
        const metodo = getFieldValue("metodo");
        const esTransferenciaFija = metodo === "transferenciaFija";

        return (
          <Row gutter={24}>
            <Col span={12}>
              <DynamicCardForm
                title="Método de Pago de la Escuela"
                style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
              >
                <Alert
                  message="Método exclusivo"
                  description="La escuela cobra con un solo método. Los alumnos de esta escuela verán únicamente esta forma de pago en el checkout."
                  type="info"
                  showIcon
                  style={{ marginBottom: 15 }}
                />
                <FormItem name="metodo" margin>
                  <SegmentedItem options={METODO_OPTIONS} style={{ width: "100%" }} />
                </FormItem>
              </DynamicCardForm>

              <DynamicCardForm
                title="Compra Directa"
                style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
              >
                <Alert
                  message="Solo disponible con transferencia fija"
                  description="Al iniciar sesión, el alumno va directo a pagar su paquete pendiente (sin catálogo ni carrito). Solo aplica cuando el método de la escuela es transferencia fija, porque ahí se genera una ficha de pago sin depender de una pasarela."
                  type={esTransferenciaFija ? "info" : "warning"}
                  showIcon
                  style={{ marginBottom: 15 }}
                />
                <FormItem
                  label="Activar compra directa"
                  name={["compraDirecta", "activa"]}
                  margin
                  valuePropName="checked"
                >
                  <Switch disabled={!esTransferenciaFija} />
                </FormItem>
              </DynamicCardForm>
            </Col>
            <Col span={12}>
              {metodo === "mattilda" && (
                <DynamicCardForm
                  title="Mattilda Payments"
                  style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
                >
                  {this.renderCamposMattilda(credencialesBloqueadas)}
                </DynamicCardForm>
              )}
              {metodo === "transferenciaFija" && (
                <DynamicCardForm
                  title="Transferencia Fija (Cuenta de la Escuela)"
                  style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
                >
                  <Alert
                    message="Conciliación manual"
                    description="El comprador ve todas las cuentas de abajo y paga a la que le convenga, usando la referencia única generada por venta. El pago se confirma manualmente desde Ventas Ecommerce y las ventas no pagadas se cancelan al vencer el plazo."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 15 }}
                  />

                  <FormItem
                    label="Instrucciones generales para el comprador"
                    name={["transferenciaFija", "instrucciones"]}
                    margin
                    tooltip="Texto introductorio, visible arriba de todas las cuentas, en el checkout, la ficha de pago y el correo. Admite HTML (puedes pegarlo directamente en modo 'HTML crudo')."
                  >
                    <InstruccionesEditor placeholder="Ej. Elige cualquiera de las siguientes cuentas y usa la referencia como concepto de pago." />
                  </FormItem>

                  <div style={{ margin: "4px 0 12px 0", fontWeight: 600, color: "#374151" }}>
                    Cuentas bancarias
                  </div>
                  <Form.List name={["transferenciaFija", "bancos"]}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.length === 0 && (
                          <Alert
                            message="Agrega al menos un banco para poder cobrar por transferencia fija."
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                          />
                        )}
                        {fields.map(({ key, name, ...restField }) => {
                          const rutaCuenta = ["transferenciaFija", "bancos", name, "cuenta"];
                          const rutaClabe = ["transferenciaFija", "bancos", name, "clabe"];
                          const rutaTarjeta = ["transferenciaFija", "bancos", name, "numeroTarjeta"];
                          const validarAlgunDatoDePago = async () => {
                            const cuenta = this.formRef.current?.getFieldValue(rutaCuenta);
                            const clabe = this.formRef.current?.getFieldValue(rutaClabe);
                            const numeroTarjeta = this.formRef.current?.getFieldValue(rutaTarjeta);
                            if (!cuenta && !clabe && !numeroTarjeta) {
                              throw new Error("Captura al menos un dato de pago (cuenta, CLABE o tarjeta)");
                            }
                          };
                          return (
                            <div
                              key={key}
                              style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: 16,
                                marginBottom: 12,
                                position: "relative",
                              }}
                            >
                              <MinusCircleOutlined
                                style={{ position: "absolute", top: 14, right: 14, fontSize: 16, color: "#ff4d4f", cursor: "pointer" }}
                                onClick={() => remove(name)}
                              />
                              <Row gutter={8}>
                                <Col span={12}>
                                  <FormItem
                                    {...restField}
                                    label="Beneficiario"
                                    name={[name, "beneficiario"]}
                                    margin
                                    rules={[{ required: true, message: "Ingresa el beneficiario" }]}
                                  >
                                    <InputItem placeholder="Nombre del titular de la cuenta" />
                                  </FormItem>
                                </Col>
                                <Col span={12}>
                                  <FormItem
                                    {...restField}
                                    label="Banco"
                                    name={[name, "banco"]}
                                    margin
                                    rules={[{ required: true, message: "Ingresa el banco" }]}
                                  >
                                    <InputItem placeholder="Ej. BBVA" />
                                  </FormItem>
                                </Col>
                              </Row>
                              <Row gutter={8}>
                                <Col span={8}>
                                  <FormItem
                                    {...restField}
                                    label="Cuenta"
                                    name={[name, "cuenta"]}
                                    margin
                                    dependencies={[rutaClabe, rutaTarjeta]}
                                    rules={[
                                      { pattern: /^\d{5,20}$/, message: "Solo dígitos (5 a 20)" },
                                      { validator: validarAlgunDatoDePago },
                                    ]}
                                  >
                                    <InputItem placeholder="Opcional" maxLength={20} />
                                  </FormItem>
                                </Col>
                                <Col span={8}>
                                  <FormItem
                                    {...restField}
                                    label="CLABE"
                                    name={[name, "clabe"]}
                                    margin
                                    dependencies={[rutaCuenta, rutaTarjeta]}
                                    rules={[
                                      { pattern: /^\d{18}$/, message: "18 dígitos exactos" },
                                      { validator: validarAlgunDatoDePago },
                                    ]}
                                  >
                                    <InputItem placeholder="Opcional, 18 dígitos" maxLength={18} />
                                  </FormItem>
                                </Col>
                                <Col span={8}>
                                  <FormItem
                                    {...restField}
                                    label="Número de tarjeta"
                                    name={[name, "numeroTarjeta"]}
                                    margin
                                    dependencies={[rutaCuenta, rutaClabe]}
                                    rules={[
                                      { pattern: /^\d{13,19}$/, message: "13 a 19 dígitos" },
                                      { validator: validarAlgunDatoDePago },
                                    ]}
                                  >
                                    <InputItem placeholder="Opcional, 13 a 19 dígitos" maxLength={19} />
                                  </FormItem>
                                </Col>
                              </Row>
                              <FormItem
                                {...restField}
                                label="Instrucciones de este banco"
                                name={[name, "instrucciones"]}
                                margin
                                tooltip="Banner propio de esta cuenta, se muestra debajo de sus datos en el checkout y la ficha de pago."
                              >
                                <InstruccionesEditor placeholder="Ej. Realiza tu depósito en cajero automático, transferencia bancaria o pago en ventanilla." />
                              </FormItem>
                            </div>
                          );
                        })}
                        <ButtonItem type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                          Agregar banco
                        </ButtonItem>
                      </>
                    )}
                  </Form.List>

                  <Row gutter={8} style={{ marginTop: 15 }}>
                    <Col span={12}>
                      <FormItem
                        label="Días para pagar (expiración)"
                        name={["transferenciaFija", "diasExpiracion"]}
                        margin
                        tooltip="Si el pago no se confirma en este plazo, la venta se cancela automáticamente y se libera el inventario."
                      >
                        <InputNumberItem min={1} placeholder="Ej. 7" style={{ width: "100%" }} />
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <FormItem
                        label="Mostrar aviso de caducidad en la ficha de pago"
                        name={["transferenciaFija", "mostrarAvisoExpiracion"]}
                        margin
                        valuePropName="checked"
                      >
                        <Switch />
                      </FormItem>
                    </Col>
                  </Row>
                  <Row gutter={8}>
                    <Col span={12}>
                      <FormItem
                        label="Texto del botón en el Checkout"
                        name={["transferenciaFija", "leyendaBotonCheckout"]}
                        margin
                        tooltip="Botón principal del checkout normal cuando el método es transferencia fija: aquí no se paga en línea, se genera la ficha con los datos para transferir."
                      >
                        <InputItem placeholder="Ej. Ir a generar ficha de pago" />
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <FormItem
                        label="Texto del botón en Compra Directa"
                        name={["transferenciaFija", "leyendaBotonFicha"]}
                        margin
                        tooltip="Botón de la pantalla de 'Compra Directa' que agrega el paquete al carrito y pasa al checkout."
                      >
                        <InputItem placeholder="Ej. Generar ficha de pago" />
                      </FormItem>
                    </Col>
                  </Row>
                </DynamicCardForm>
              )}
              {metodo !== "mattilda" && metodo !== "transferenciaFija" && (
                <DynamicCardForm
                  title="Conekta"
                  style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
                >
                  {this.renderCamposConekta(credencialesBloqueadas)}
                </DynamicCardForm>
              )}
            </Col>
          </Row>
        );
      }}
    </Form.Item>
  );

  render() {
    const credencialesBloqueadas = !this.props.permisoAnadirModificarTokens;
    const {
      escuelas,
      escuelaSeleccionadaId,
      configEscuelaExiste,
      configEscuelaActiva,
      loadingEscuela,
    } = this.state;

    const nombreEscuelaActual =
      escuelas.find((e) => e._id === escuelaSeleccionadaId)?.nombre || "";

    return (
      <>
        <Spin spinning={this.state.loading || loadingEscuela}>
          {this.props.escuela && (
            <DynamicCardForm
              title="Configuración por Escuela"
              style={{ marginBottom: 16 }}
            >
              <Alert
                message="¿Cuándo se aplica cada configuración?"
                description="La configuración general aplica a compradores sin sesión iniciada y a alumnos cuya escuela no tenga configuración propia activa. La configuración por escuela solo aplica a alumnos que iniciaron sesión con una escuela asignada."
                type="info"
                showIcon
                style={{ marginBottom: 14 }}
              />
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <SelectItem
                    allowClear
                    placeholder="Selecciona una escuela para configurar su método de pago"
                    value={escuelaSeleccionadaId}
                    onChange={(val) => {
                      if (!val) {
                        this.onDeseleccionarEscuela();
                      } else {
                        this.onSelectEscuela(val);
                      }
                    }}
                    style={{ width: "100%" }}
                  >
                    {escuelas.map((e) => (
                      <Option key={e._id} value={e._id}>
                        {e.nombre}
                      </Option>
                    ))}
                  </SelectItem>
                </Col>
                {escuelaSeleccionadaId && configEscuelaExiste && configEscuelaActiva && (
                  <Col>
                    <ButtonItem danger onClick={this.desactivarConfigEscuela}>
                      Usar configuración general
                    </ButtonItem>
                  </Col>
                )}
                {escuelaSeleccionadaId && configEscuelaExiste && !configEscuelaActiva && (
                  <Col>
                    <ButtonItem type="primary" onClick={this.activarConfigEscuela}>
                      Activar configuración propia
                    </ButtonItem>
                  </Col>
                )}
              </Row>

              {escuelaSeleccionadaId && !configEscuelaExiste && (
                <Alert
                  message={`"${nombreEscuelaActual}" usa la configuración general de la empresa`}
                  description="Configura el método de pago y guarda para crear una configuración propia para esta escuela."
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              )}

              {escuelaSeleccionadaId && configEscuelaExiste && configEscuelaActiva && (
                <Alert
                  message={`Configuración propia activa para "${nombreEscuelaActual}"`}
                  type="success"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              )}

              {escuelaSeleccionadaId && configEscuelaExiste && !configEscuelaActiva && (
                <Alert
                  message={`"${nombreEscuelaActual}" está usando la configuración general`}
                  description="La configuración propia está guardada pero inactiva. Pulsa 'Activar configuración propia' para que esta escuela use su config especial."
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              )}
            </DynamicCardForm>
          )}

          <Form
            name="apisConfiguracion"
            layout="vertical"
            onFinish={this.onFinish}
            onFinishFailed={this.onFinishFailed}
            onValuesChange={this.handleValuesChange}
            ref={this.formRef}
            loading={this.state.loading}
          >
            {escuelaSeleccionadaId
              ? this.renderFormEscuela(credencialesBloqueadas)
              : this.renderFormEmpresa(credencialesBloqueadas)}

            <div style={{ marginTop: 24 }} />
            <hr style={{ margin: "20px 0" }} />

            <ButtonItem
              type="primary"
              htmlType="submit"
              style={{ display: "block", margin: "0 0 0 auto" }}
            >
              {escuelaSeleccionadaId
                ? configEscuelaExiste
                  ? `Guardar config. de "${nombreEscuelaActual}"`
                  : `Crear config. propia para "${nombreEscuelaActual}"`
                : "Guardar Configuración"}
            </ButtonItem>
          </Form>
        </Spin>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
    escuela: state.empresa.escuela,
    permisoAnadirModificarTokens:
      state.user.tipo === 0 ||
      state.user.permisos.find(
        (valueTodos) =>
          valueTodos.permisoId.toString() === "6a1db0a98958639144fd5f0e",
      )?.activado,
  };
};
export default connect(mapStateToProps, null, null, { forwardRef: true })(
  ApisConfiguracion,
);
